import React, { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { ChatCircle, ArrowSquareOut, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const flexvuiSteps = [{
  number: '01',
  title: 'Find: Problem identification',
  desc: 'Identify the right problems and select the appropriate NLU platforms to evaluate against them.'
}, {
  number: '02',
  title: 'Listen: System analysis',
  desc: 'Analyze existing systems, gather conversational data, and pull signal from where real conversations were already happening.'
}, {
  number: '03',
  title: 'Empathize: User research',
  desc: 'Conduct user interviews and map customer journeys. Understand the actual call before designing for it.'
}, {
  number: '04',
  title: 'eXtract: Data parsing',
  desc: 'Parse 16,000+ unstructured customer messages to identify intents and conversation patterns.'
}, {
  number: '05',
  title: 'Version: Prototyping',
  desc: 'Build initial prototypes from conversational maps. Two finalists, Watson and Dialogflow, in parallel.'
}, {
  number: '06',
  title: 'Upskill: Testing and refinement',
  desc: 'Test with users and refine against training data. Tune the model where it matters.'
}, {
  number: '07',
  title: 'Introduce: Launch and validation',
  desc: 'Validate prototypes with stakeholders. Secure Phase 2 funding to take it past POC.'
}];
export function CaseStudyAIComparison({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <ReadingProgress color="#3B82F6" data-id="element-504" />
      {!isOverlay && <CustomCursor data-id="element-505" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-506">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-507">
          {!isOverlay && <CaseStudyHeader data-id="element-508" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-509" />}

          <main className="flex-1 w-full pb-32" data-id="element-510">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-511">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-512">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-513" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-514">
                <ChatCircle className="w-8 h-8 text-[#3B82F6]" weight="light" data-id="element-515" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light text-slate-900 leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-516">
                Conversational AI
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-517">
                Validating conversational AI for 100,000+ monthly calls.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-518">
                MoneyGram's Innovation Lab needed to know if conversational AI
                could absorb their customer service volume. This was my first
                conversational design project, with no internal playbook to lean
                on. I built one as I went, then proved it with working
                prototypes the team could actually talk to.
              </p>
            </motion.section>

            {/* HERO IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-519">
              <div className="bg-slate-100 rounded-2xl py-10 sm:py-14 md:py-16 px-6 sm:px-10 md:px-14 flex items-center justify-center" data-id="element-520">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png" alt="Conversational AI platform interface" className="w-full max-w-none object-contain drop-shadow-xl" data-id="element-521" />
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-522">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-523">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-12" data-id="element-524">
                  <div data-id="element-525">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900 mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-526">
                      Client
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-527">
                      MoneyGram Innovation Lab
                    </div>
                  </div>
                  <div data-id="element-528">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900 mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-529">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-530">
                      Conversational AI Designer
                    </div>
                  </div>
                  <div data-id="element-531">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900 mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-532">
                      Platforms
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-533">
                      Watson, Dialogflow
                    </div>
                  </div>
                  <div data-id="element-534">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900 mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-535">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-536">
                      Conversational design, NLU, Methodology, Prototyping
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-537">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-900 mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-538">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-539">
                    Mateo Cohen (Conversational AI Designer), MoneyGram
                    Innovation Director and Lead Innovation Manager, Agency
                    project team
                  </div>
                </div>
              </div>
            </motion.section>

            {/* QUOTE-OVERLAY IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-540">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-541">
                <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600&q=80" alt="Customer service operations" className="absolute inset-0 w-full h-full object-cover" data-id="element-542" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/45 to-slate-900/25" data-id="element-543"></div>

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-544">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-545">
                    "Voice carrying questions a chat could absorb, overloading
                    systems and customers."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-546">
                    Initial diagnosis
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-547">
              {/* Problem */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-548">
                <div className="flex items-center gap-3 mb-4" data-id="element-549">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#0f172a'
                }} data-id="element-550">
                    01
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#3B82F6'
                }} aria-hidden="true" data-id="element-551" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-552">
                  Problem: A call volume nobody could absorb manually
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-553">
                  MoneyGram was handling 100,000+ monthly customer service calls
                  with limited automation, and a large share of that volume was
                  for questions a chat interface could absorb without a live
                  agent. The result was an overloaded voice channel and
                  frustrated customers. The Innovation Lab needed to evaluate
                  conversational AI platforms and prove technical feasibility
                  before committing to a build. What they didn't yet have was a
                  way to route the right requests into the right channels, the
                  platform expertise to build it, or a working proof of concept.
                </p>

                <div className="bg-slate-100 rounded-lg p-6 sm:p-8 my-10" data-id="element-554">
                  <div className="text-xl sm:text-2xl font-light text-slate-900" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-555">
                    100,000+ monthly calls. 16,000+ unstructured customer
                    messages to parse.
                  </div>
                </div>
              </motion.div>

              {/* Research */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-556">
                <div className="flex items-center gap-3 mb-4" data-id="element-557">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#0f172a'
                }} data-id="element-558">
                    02
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#3B82F6'
                }} aria-hidden="true" data-id="element-559" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-560">
                  Research: Self-taught, then formalized
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-561">
                  I taught myself conversational design from scratch. Audited
                  50+ platforms, narrowed to five enterprise finalists, and dug
                  into MoneyGram's real call data to find the intents that
                  actually mattered.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-562">
                  Then I formalized what I'd learned into FLEXVUI: a seven-phase
                  framework combining Google Design Sprint principles with
                  conversational AI best practices. The agency adopted it across
                  VUI projects.
                </p>
              </motion.div>

              {/* Process: FLEXVUI Framework */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-563">
                <div className="flex items-center gap-3 mb-4" data-id="element-564">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#0f172a'
                }} data-id="element-565">
                    03
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#3B82F6'
                }} aria-hidden="true" data-id="element-566" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-567">
                  Process: The FLEXVUI framework
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-568">
                  Seven phases. Find, Listen, Empathize, eXtract, Version,
                  Upskill, Introduce. Each one mapped to a deliverable, each one
                  validated against MoneyGram's actual call data.
                </p>

                <div data-id="element-569">
                  {flexvuiSteps.map((step, idx) => <div key={step.number} className="relative flex gap-5 sm:gap-8 pb-12 sm:pb-16 last:pb-0" data-id="element-570">
                      {idx !== flexvuiSteps.length - 1 && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-571" />}
                      <div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-300 flex items-center justify-center flex-shrink-0" data-id="element-572">
                        <span className="text-xs sm:text-sm font-medium text-slate-900" style={{
                      fontFamily: "'Ronzino', sans-serif"
                    }} data-id="element-573">
                          {step.number}
                        </span>
                      </div>
                      <div className="flex-1 pt-1.5 sm:pt-2.5" data-id="element-574">
                        <h4 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{
                      fontFamily: "'Gefika', sans-serif"
                    }} data-id="element-575">
                          {step.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed max-w-2xl" style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }} data-id="element-576">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>

              {/* Solution */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-577">
                <div className="flex items-center gap-3 mb-4" data-id="element-578">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#0f172a'
                }} data-id="element-579">
                    04
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#3B82F6'
                }} aria-hidden="true" data-id="element-580" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-581">
                  Solution: Two prototypes, one direction
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-582">
                  I built working prototypes in IBM Watson and Google Dialogflow
                  in parallel. Same intents, same data, same evaluation rubric.
                  Demos that ran end-to-end, not slideware. This was what
                  unlocked stakeholder buy-in: a recommendation backed by
                  something they could actually talk to.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-583">
                  Along the way I also got into a working conversation with
                  Braden Ream, Voiceflow's founder. It wasn't a vendor pitch or
                  an endorsement, just a peer-level discussion about where voice
                  was going as a discipline. Those kinds of conversations
                  sharpened the conversational design philosophy I was already
                  prototyping internally.
                </p>
              </motion.div>

              {/* Outcome */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-584">
                <div className="flex items-center gap-3 mb-4" data-id="element-585">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#0f172a'
                }} data-id="element-586">
                    05
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#3B82F6'
                }} aria-hidden="true" data-id="element-587" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-588">
                  Outcome: Phase 2 funded
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-589">
                  <div data-id="element-590">
                    <div className="text-4xl sm:text-5xl font-light text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-591">
                      50+
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-592">
                      Platforms evaluated, 5 finalists
                    </div>
                  </div>
                  <div data-id="element-593">
                    <div className="text-4xl sm:text-5xl font-light text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-594">
                      2
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-595">
                      Working prototypes (Watson, Dialogflow)
                    </div>
                  </div>
                  <div data-id="element-596">
                    <div className="text-4xl sm:text-5xl font-light text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-597">
                      $1M+
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-598">
                      Phase 2 funding secured
                    </div>
                  </div>
                  <div data-id="element-599">
                    <div className="text-4xl sm:text-5xl font-light text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-600">
                      1
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-601">
                      Methodology (FLEXVUI) adopted agency-wide
                    </div>
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-602">
                  A first-of-its-kind project became a repeatable practice:
                  working POCs proved feasibility, the next funding round got
                  unlocked, and FLEXVUI outlived the engagement as the agency's
                  playbook for voice work.
                </p>
              </motion.div>

              {/* Documentation */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-603">
                <div className="flex items-center gap-3 mb-4" data-id="element-604">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#0f172a'
                }} data-id="element-605">
                    06
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#3B82F6'
                }} aria-hidden="true" data-id="element-606" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-607">
                  Documentation: Full project deck
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-3" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-608">
                  The full breakdown of platform analysis, FLEXVUI phases, and
                  conversational flow designs.
                </p>
                <p className="text-xs sm:text-sm text-slate-500 italic leading-relaxed mb-8 max-w-2xl" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-609">
                  Note: This is a remake of the original deck presented to
                  MoneyGram's Innovation Lab, rebuilt here for overall project
                  context and shareability.
                </p>

                <div className="relative w-full bg-slate-50 rounded-2xl overflow-hidden border border-slate-200" data-id="element-610">
                  <a href="https://gamma.app/embed/tpccpfnptxadvaw" target="_blank" rel="noopener noreferrer" className="absolute top-4 right-4 z-10 bg-white/95 hover:bg-white text-slate-900 px-4 py-2 rounded-full text-xs font-medium shadow-lg flex items-center gap-2 transition-all duration-300 backdrop-blur-sm" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-611">
                    <ArrowSquareOut className="w-4 h-4" data-id="element-612" />
                    Open fullscreen
                  </a>
                  <div className="relative w-full h-[440px] sm:h-auto sm:aspect-[14/9]" data-id="element-613">
                    <iframe src="https://gamma.app/embed/tpccpfnptxadvaw" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }} allow="fullscreen" title="MoneyGram Innovation Lab" data-id="element-614" />
                  </div>
                </div>
              </motion.div>
            </section>

            <CaseStudyNavigation previousCase={{
            category: 'Digital Protection',
            title: 'Cyber Security Platform',
            slug: 'cyber-security'
          }} data-id="element-615" />
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-616" />
      <ChatDrawer data-id="element-617" />
      <MobileBottomCTA data-id="element-618" />
      {!isOverlay && <Footer data-id="element-619" />}
    </>;
}